import { EImportSectionsTarget } from '@constants';
import { IActivity } from '@sdk/contracts';
import { Drawer } from 'antd';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ActivitiesList } from './ActivitiesList';
import { ContentWrapper, SelectorWrapper, Label, CustomAlert } from './ImportActivityDrawer.styles';
import { useImportActivityDrawer } from './useImportActivityDrawer';

import { CourseSelector } from '@/components/Form';
import { Icon } from '@/components/Icon';
import { ImportContentDrawerFooter } from '@/components/SectionManagement/ImportContentDrawerFooter';

const DRAWER_WIDTH = 747;

type ImportActivityDrawerProps = {
  target: EImportSectionsTarget;
  sectionId: string;
  fetchOnlyParentsCourses?: boolean;
  isImporting?: boolean;
};

export const ImportActivityDrawer: React.FC<ImportActivityDrawerProps> = ({
  target,
  sectionId,
  fetchOnlyParentsCourses,
  isImporting
}) => {
  const { t } = useTranslation(['ManagerBaseContentDetailTranslations', 'DefaultTranslations']);
  const { isOpen, closeDrawer, onImport, sectionId: selectedSectionId } = useImportActivityDrawer();
  const [selectedCourseId, setSelectedCourseId] = useState<string>();
  const [selectedActivities, setSelectedActivities] = useState<IActivity[]>([]);
  const [isSyncedToParent, setIsSyncedToParent] = useState(false);
  const shouldOpen = isOpen && selectedSectionId === sectionId;

  const handleClose = () => {
    if (isImporting) return;

    setSelectedCourseId(undefined);
    setSelectedActivities([]);
    setIsSyncedToParent(false);
    closeDrawer();
  };

  const handleImport = () => {
    if (!selectedActivities.length || !onImport) return;

    onImport({ activities: selectedActivities, isSyncedToParent, onSuccess: handleClose });
  };

  const handleCourseChange = (courseId: string) => {
    setSelectedCourseId(courseId);
    setSelectedActivities([]);
  };

  return (
    <Drawer
      open={shouldOpen}
      onClose={handleClose}
      destroyOnHidden
      width={DRAWER_WIDTH}
      title={t('IMPORT_ACTIVITY')}
      footer={
        <ImportContentDrawerFooter
          isLoading={isImporting}
          isSyncedToParent={isSyncedToParent}
          onSyncChange={setIsSyncedToParent}
          onCancel={handleClose}
          onImport={handleImport}
          isImportDisabled={!selectedActivities.length}
          showSyncCheckbox={fetchOnlyParentsCourses}
          isActivityImport
        />
      }>
      <ContentWrapper>
        <CustomAlert
          message={t('ManagerBaseContentDetailTranslations:IMPORT_ACTIVITY_ALERT')}
          type="info"
          showIcon
          icon={<Icon i="InformationFilled" size={18} />}
        />
        <SelectorWrapper>
          <Label>{t('ManagerBaseContentDetailTranslations:COURSE_SELECTOR_LABEL')}</Label>
          <CourseSelector
            value={selectedCourseId}
            onChange={handleCourseChange}
            styles={{ width: '100%' }}
            filters={
              fetchOnlyParentsCourses !== undefined
                ? {
                    isParent: fetchOnlyParentsCourses
                  }
                : undefined
            }
          />
        </SelectorWrapper>
        <ActivitiesList
          courseId={selectedCourseId}
          selectedActivities={selectedActivities}
          onSelectionChange={setSelectedActivities}
          target={target}
        />
      </ContentWrapper>
    </Drawer>
  );
};
